import {
  EffectFiveDirections,
  EffectFourCorners,
  EffectFourDirections,
  EffectTwoAxes,
  EffectThreeAxes,
  EffectNineDirections,
  EffectPower,
  EffectTwoDirections,
  SchemaNumber,
  SchemaString,
  EffectFiveCorners,
  EffectEightDirections,
  EffectRangeAll,
  EffectRangeInOut,
  Easing,
  Shape,
  AngleEightDiagonals,
  SchemaBoolean,
  MouseEasing,
  EffectAxis,
  SchemaNumberWithUnit,
} from '../types';

export const duration: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 4000,
  step: 100,
  default: 0,
};

export const durationShort: SchemaNumber = {
  type: 'number',
  min: 100,
  max: 4000,
  step: 100,
  default: 2000,
};

export const durationMedium: SchemaNumber = {
  type: 'number',
  min: 100,
  max: 15000,
  step: 100,
  default: 2000,
};

export const durationLong: SchemaNumber = {
  type: 'number',
  min: 100,
  max: 50000,
  step: 100,
  default: 5000,
};

export const spins: SchemaNumber = {
  type: 'number',
  min: 0.1,
  max: 4,
  step: 0.05,
  default: 1,
};

export const delay: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 8000,
  step: 100,
  default: 0,
};

export const allowReplay: SchemaString<'perPageView' | 'never'> = {
  type: 'string',
  enum: [
    'perPageView',
    // 'always',
    'never',
  ],
  default: 'perPageView',
};

export const power: SchemaString<EffectPower> = {
  type: 'string',
  enum: ['soft', 'medium', 'hard'],
  default: 'medium',
};

export const directionTwoSides: SchemaString<EffectTwoDirections> = {
  type: 'string',
  enum: ['left', 'right'],
  default: 'right',
};

export const directionTwoAxes: SchemaString<EffectTwoAxes> = {
  type: 'string',
  enum: ['horizontal', 'vertical'],
  default: 'vertical',
};

export const directionThreeAxes: SchemaString<EffectThreeAxes> = {
  type: 'string',
  enum: ['horizontal', 'vertical', 'center'],
  default: 'vertical',
};

export const directionFourSides: SchemaString<EffectFourDirections> = {
  type: 'string',
  enum: ['left', 'right', 'top', 'bottom'],
  default: 'right',
};

export const directionFiveSides: SchemaString<EffectFiveDirections> = {
  type: 'string',
  enum: ['left', 'right', 'top', 'bottom', 'center'],
  default: 'right',
};

export const directionEightSides: SchemaString<EffectEightDirections> = {
  type: 'string',
  enum: [
    'right',
    'top-right',
    'top',
    'top-left',
    'left',
    'bottom-left',
    'bottom',
    'bottom-right',
  ],
  default: 'right',
};

export const directionNineSides: SchemaString<EffectNineDirections> = {
  type: 'string',
  enum: [
    'right',
    'top-right',
    'top',
    'top-left',
    'left',
    'bottom-left',
    'bottom',
    'bottom-right',
    'center',
  ],
  default: 'right',
};

export const directionFourCorners: SchemaString<EffectFourCorners> = {
  type: 'string',
  enum: ['top-left', 'bottom-left', 'top-right', 'bottom-right'],
  default: 'top-left',
};

export const directionFiveCorners: SchemaString<EffectFiveCorners> = {
  type: 'string',
  enum: ['top-left', 'bottom-left', 'top-right', 'bottom-right', 'center'],
  default: 'top-left',
};

export const directionSpin: SchemaString<'clockwise' | 'counter-clockwise'> = {
  type: 'string',
  enum: ['clockwise', 'counter-clockwise'],
  default: 'clockwise',
};

export const angleEightDiagonals: SchemaNumber<AngleEightDiagonals> = {
  type: 'number',
  enum: [30, 60, 120, 150, 210, 240, 300, 330],
  default: 210,
};

export const angle: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 360,
  step: 1,
  default: 270,
};

export const opacity: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 1,
  step: 0.01,
  default: 0,
};

export const intensity: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 1,
  step: 0.1,
  default: 0,
};

export const clipShapes: SchemaString<Shape> = {
  type: 'string',
  enum: ['ellipse', 'circle', 'rectangle', 'diamond'], // , 'window'],
  default: 'circle',
};

export const easing: SchemaString<Easing> = {
  type: 'string',
  enum: [
    'linear',

    'cubicIn',
    'sineIn',
    'quintIn',

    'cubicOut',
    'sineOut',
    'quintOut',
    'backOut',

    'cubicInOut',
    'sineInOut',
    'quintInOut',
  ],
  default: 'sineInOut',
};

export const mouseEasing: SchemaString<MouseEasing> = {
  type: 'string',
  enum: ['linear', 'hardBackOut', 'easeOut'],
  default: 'easeOut',
};

export const mouseBounceEasing: SchemaString<MouseEasing> = {
  type: 'string',
  enum: ['elastic', 'bounce'],
  default: 'elastic',
};

export const easingExcludingBackOut: SchemaString<Easing> = {
  type: 'string',
  enum: [
    'linear',

    'cubicIn',
    'sineIn',
    'quintIn',

    'cubicOut',
    'sineOut',
    'quintOut',

    'cubicInOut',
    'sineInOut',
    'quintInOut',
  ],
  default: 'sineInOut',
};

export const range: SchemaString<EffectRangeAll> = {
  type: 'string',
  enum: ['in', 'out', 'continuous'],
  default: 'in',
};

export const rangeInOut: SchemaString<EffectRangeInOut> = {
  type: 'string',
  enum: ['in', 'out'],
  default: 'in',
};

export const axis: SchemaString<EffectAxis> = {
  type: 'string',
  enum: ['horizontal', 'vertical', 'both'],
  default: 'both',
};

export const scrollOffsetIn: SchemaNumber = {
  type: 'number',
  default: 50,
  min: 0,
  max: 50,
};

export const scrollOffsetOut: SchemaNumber = {
  type: 'number',
  default: 50,
  min: 50,
  max: 100,
};

export const scrollOffsetFull: SchemaNumber = {
  type: 'number',
  default: 0,
  min: 0,
  max: 100,
};

export const screenEnter: SchemaNumber = {
  type: 'number',
  default: 0,
  min: 0,
  max: 0,
  hidden: true,
};

export const screenCenter: SchemaNumber = {
  type: 'number',
  default: 50,
  min: 50,
  max: 50,
  hidden: true,
};

export const screenLeave: SchemaNumber = {
  type: 'number',
  default: 100,
  min: 100,
  max: 100,
  hidden: true,
};

export const perspective: SchemaNumber = {
  type: 'number',
  min: 200,
  max: 1000,
  step: 50,
  default: 1000,
};

export const transitionDuration: SchemaNumber = {
  type: 'number',
  min: 0,
  max: 5000,
  step: 20,
  default: 500,
};

export const inverted: SchemaBoolean = {
  type: 'boolean',
  default: false,
};

export const mouseDistance: SchemaNumberWithUnit = {
  type: 'number-with-unit',
  min: 0,
  max: 800,
  step: 5,
  default: { value: 200, type: 'px' },
  units: ['percentage', 'px', 'vh', 'vw'],
};
